/**
 * «Что вошло и что нет» — раздел «Что здесь настоящее» (IA-04).
 *
 * Экран отвечает на один вопрос, который иначе задают устно: **как собран
 * канон и что в него не вошло**. Он не рассказывает про район — про район
 * рассказывает «Картина района»; здесь живёт то, что правило уровня прямо
 * запрещает показывать на районе: строки источника и отдельные замечания
 * качества (`lib/levels.ts`, отказ L1).
 *
 * Что изменилось и почему.
 *
 * **Сверка стоит первой.** Равенство «канон + исключённое = итог чекпоинта» —
 * это и есть причина, по которой экран открывают: пока оно сходится, всем
 * числам продукта можно верить, а если разойдётся — верить нельзя ни одному.
 * До этого оно было последним абзацем предупреждения внизу страницы.
 *
 * И оно ПРОВЕРЯЕТСЯ, а не только печатается: знак между слагаемыми и итогом
 * считает `balanceCheck`, расхождение называется величиной, а отсутствие одной
 * из величин — отсутствием. Нарисованное «=», за которым никто не складывал,
 * было бы ровно тем обещанием, ради проверки которого экран и открывают.
 *
 * **Четыре числа — про канон, а не про землю.** Контуры канона, несвязанные
 * строки источника, гектары канона и открытые замечания: три из них нельзя
 * прочитать больше нигде, а четвёртое (гектары) стоит здесь как одна сторона
 * сверки. Четырёх плашек-дублей района («Всего земель», «Пашня», «Контуры»,
 * «Хозяйства») больше нет.
 *
 * **Подробности — раскрытиями, а не вторыми экранами.** «Из чего собрано» и
 * «что в данных не сходится» лежат на шаг вглубь и в свёрнутом виде называют
 * свои величины. Разбор замечаний остаётся отдельным адресом `/data-quality`:
 * это рабочее место со своими фильтрами, а не абзац сводки.
 */

import { Link } from "react-router-dom";
import { useAnalysisContext } from "../../analysis/context";
import { api } from "../../api/client";
import { useRequest } from "../../api/useRequest";
import { layout, PageHeader } from "../../components/AppShell";
import { Disclosure } from "../../components/Disclosure";
import { districtStep, LevelTrail } from "../../components/LevelTrail";
import {
  Area,
  Button,
  Card,
  Count,
  DataStatePanel,
  DefinitionList,
  InlineAlert,
  MetricCard,
  RequestErrorPanel,
} from "../../components/ui";
import { sourceKindLabel } from "../../lib/epistemic";
import { formatCount, formatDate } from "../../lib/format";
import { COMMON } from "../../i18n/common";
import { defineMessages } from "../../i18n/define";
import { useT } from "../../i18n/hooks";
import { QualityFacets } from "../quality/DataQuality";
import { areaText } from "./areaText";

import { RegistrySummarySection } from "./RegistryOverview";

import features from "../features.module.css";
import styles from "./trust.module.css";

const m = defineMessages({
  loading: { id: "registry.trust.loading", defaultMessage: "Загружаем состав канона", description: "Заголовок состояния загрузки экрана «Что вошло и что нет» (раздел «Данные»)" },
  loadError: { id: "registry.trust.loadError", defaultMessage: "Состав канона не загрузился", description: "Заголовок ошибки загрузки экрана «Что вошло и что нет»; под ним кнопка «Повторить»" },
  title: { id: "registry.trust.title", defaultMessage: "Что вошло и что нет", description: "Заголовок экрана раздела «Данные» (хоким, менеджер): как собран канон реестра и что в него не вошло; канон — принятая версия реестра земель" },
  purposeWithSnapshot: {
    id: "registry.trust.purpose.withSnapshot",
    defaultMessage: "Как собран канон на принятом срезе «{kind}» и что в него не вошло. Всё ниже — про сам реестр, а не про положение дел в районе.",
    description: "Подзаголовок экрана «Что вошло и что нет»; {kind} — вид принятого среза реестра (подпись, например «сводный реестр»)",
  },
  purposeNoSnapshot: {
    id: "registry.trust.purpose.noSnapshot",
    defaultMessage: "Принятого среза нет: реестр ещё не публиковался, и говорить о составе канона не о чем.",
    description: "Подзаголовок экрана «Что вошло и что нет», когда реестр ещё не опубликован",
  },
  issuesButton: { id: "registry.trust.issuesButton", defaultMessage: "Разбор замечаний", description: "Кнопка в шапке экрана: переход к экрану качества данных с разбором замечаний; коротко" },
  balanceTitle: { id: "registry.trust.balance.title", defaultMessage: "Принятый срез и сверка площадей", description: "Заголовок карточки: сведения о принятом срезе реестра и сверка площадей (канон + исключённое = итог чекпоинта)" },
  stateOn: { id: "registry.trust.snapshot.stateOn", defaultMessage: "Состояние на", description: "Подпись строки списка сведений о срезе: на какую дату состояние данных" },
  source: { id: "registry.trust.snapshot.source", defaultMessage: "Источник", description: "Подпись строки списка сведений о срезе: из какого файла собран" },
  sourceValue: {
    id: "registry.trust.snapshot.sourceValue",
    defaultMessage: "{file} · вид «{kindLabel}» ({kindCode})",
    description: "Значение строки «Источник»: {file} — имя файла, {kindLabel} — подпись вида среза, {kindCode} — машинный код вида (остаётся рядом для разбора)",
  },
  checksum: { id: "registry.trust.snapshot.checksum", defaultMessage: "Контрольная сумма", description: "Подпись строки списка сведений о срезе: контрольная сумма файла источника" },
  rules: { id: "registry.trust.snapshot.rules", defaultMessage: "Правила", description: "Подпись строки списка сведений о срезе: версия правил сборки канона" },
  rulesValue: {
    id: "registry.trust.snapshot.rulesValue",
    defaultMessage: "{version}, опубликовано {date}",
    description: "Значение строки «Правила»: {version} — версия правил, {date} — дата публикации среза",
  },
  noSnapshotTitle: { id: "registry.trust.noSnapshot.title", defaultMessage: "Принятого среза нет", description: "Заголовок пустого состояния карточки сверки: реестр не публиковался" },
  noSnapshotBody: {
    id: "registry.trust.noSnapshot.body",
    defaultMessage: "Реестр ещё не публиковался: сверять нечего, и числа ниже описывают пустой канон, а не нулевой район.",
    description: "Пояснение пустого состояния карточки сверки: реестр не публиковался",
  },
  termCanon: { id: "registry.trust.balance.canon", defaultMessage: "Канон", description: "Слагаемое сверки площадей: гектары, вошедшие в канон; подпись над числом, коротко" },
  termInsideWhole: { id: "registry.trust.balance.insideWhole", defaultMessage: "Части внутри контуров", description: "Слагаемое сверки площадей: гектары частей контуров, у которых в реестре есть и строка «целиком»; части лежат внутри контура и в канон не складываются; подпись над числом, коротко" },
  insideWholeNote: {
    id: "registry.trust.balance.insideWholeNote",
    defaultMessage: "Не сложено: {contours, plural, one {# контур заявлен} few {# контура заявлены} many {# контуров заявлено} other {# контура заявлены}} и целиком, и частями. Части лежат внутри контура «целиком» и в каноне не складываются с ним (контур не суммируется со своими частями); чекпоинт сложил обе строки.",
    description: "Пояснение под сверкой площадей: сколько контуров не сложено с их частями; {contours} — число контуров",
  },
  termExcluded: { id: "registry.trust.balance.excluded", defaultMessage: "Исключённое", description: "Слагаемое сверки площадей: гектары, исключённые из-за конфликта номеров; подпись над числом, коротко" },
  termDropped: { id: "registry.trust.balance.dropped", defaultMessage: "Отброшенные дубли", description: "Слагаемое сверки площадей: гектары строк, признанных той же землёй (дубли); подпись над числом, коротко" },
  termCheckpoint: { id: "registry.trust.balance.checkpoint", defaultMessage: "Итог чекпоинта", description: "Итог сверки площадей: контрольная сумма площади по файлу источника (чекпоинт); подпись над числом, коротко" },
  offTitle: { id: "registry.trust.balance.off.title", defaultMessage: "Сверка не сходится", description: "Заголовок предупреждения: слагаемые сверки площадей расходятся с итогом" },
  offBody: {
    id: "registry.trust.balance.off.body",
    defaultMessage: "Слагаемые расходятся с итогом чекпоинта на {diff} ({direction}). Пока расхождение не объяснено, числам этого среза верить нельзя — ни одному, а не только площадям.",
    description: "Текст предупреждения о расхождении сверки; {diff} — площадь расхождения с единицей, {direction} — «канона больше» или «канона меньше»",
  },
  offMore: { id: "registry.trust.balance.off.more", defaultMessage: "канона больше", description: "Направление расхождения сверки, в скобках после величины: площадь канона больше итога; строчными" },
  offLess: { id: "registry.trust.balance.off.less", defaultMessage: "канона меньше", description: "Направление расхождения сверки, в скобках после величины: площадь канона меньше итога; строчными" },
  okNote: { id: "registry.trust.balance.ok", defaultMessage: "Сверка сходится: сумма слагаемых равна итогу чекпоинта.", description: "Итог сверки площадей: слагаемые равны итогу" },
  unknownNote: {
    id: "registry.trust.balance.unknown",
    defaultMessage: "Сверять не с чем: одной из четырёх величин в срезе нет, и подставлять ей ноль нельзя — отсутствие не сходится и не расходится.",
    description: "Итог сверки площадей, когда одной из четырёх величин нет в данных",
  },
  kpiContours: { id: "registry.trust.kpi.contours.label", defaultMessage: "Контуров в каноне", description: "Подпись крупной плашки KPI на экране «Что вошло и что нет»: число контуров канона" },
  kpiContoursNote: {
    id: "registry.trust.kpi.contours.note",
    defaultMessage: "из {rows} строк источника; частей контуров: {parts}",
    description: "Мелкая подпись под плашкой «Контуров в каноне»; {rows} — число строк источника, {parts} — число частей контуров",
  },
  kpiUnlinked: { id: "registry.trust.kpi.unlinked.label", defaultMessage: "Строк без связи", description: "Подпись плашки KPI: строки источника, не связанные ни с одним субъектом" },
  kpiUnlinkedNote: {
    id: "registry.trust.kpi.unlinked.note",
    defaultMessage: "остались в приёмке: субъекта у них нет, и контурами они не называются",
    description: "Мелкая подпись под плашкой «Строк без связи»; «приёмка» — этап проверки загруженного файла; субъект — хозяйство или иной владелец земли",
  },
  kpiHectares: { id: "registry.trust.kpi.hectares.label", defaultMessage: "Гектаров в каноне", description: "Подпись плашки KPI: общая площадь земли канона в гектарах" },
  kpiHectaresNote: {
    id: "registry.trust.kpi.hectares.note",
    defaultMessage: "{rows, plural, one {# строка} few {# строки} many {# строк} other {# строки}} площади",
    description: "Мелкая подпись под плашкой «Гектаров в каноне»: сколько строк площади легло в сумму; {rows} — число",
  },
  kpiHectaresMissing: {
    id: "registry.trust.kpi.hectares.noteMissing",
    defaultMessage: "{rows, plural, one {# строка} few {# строки} many {# строк} other {# строки}} площади, без значения: {missing}",
    description: "Мелкая подпись под плашкой «Гектаров в каноне», когда у части строк площади нет значения; {rows} — число строк, {missing} — сколько из них без значения",
  },
  kpiHectaresNone: { id: "registry.trust.kpi.hectares.none", defaultMessage: "строк площади в срезе нет", description: "Мелкая подпись под плашкой «Гектаров в каноне», когда строк площади нет" },
  kpiIssues: { id: "registry.trust.kpi.issues.label", defaultMessage: "Замечаний открыто", description: "Подпись плашки KPI: число открытых замечаний качества данных" },
  kpiIssuesNote: {
    id: "registry.trust.kpi.issues.note",
    defaultMessage: "блокирующих из них: {blockers}; всего замечаний среза: {total}",
    description: "Мелкая подпись под плашкой «Замечаний открыто»; {blockers} — блокирующие (останавливают публикацию), {total} — все замечания среза",
  },
  blockersTitle: {
    id: "registry.trust.blockers.title",
    defaultMessage: "Блокирующих замечаний качества: {count}",
    description: "Заголовок предупреждения о блокирующих замечаниях; {count} — число",
  },
  blockersBody: {
    id: "registry.trust.blockers.body",
    defaultMessage: "Публикация записи по ним не выполняется — это не оценка данных, а остановка. <link>Открыть их список</link>.",
    description: "Текст предупреждения о блокирующих замечаниях; <link> — ссылка на список замечаний",
  },
  compositionTitle: { id: "registry.trust.composition.title", defaultMessage: "Из чего собран канон", description: "Заголовок раскрывающегося блока: состав канона" },
  compositionSummary: {
    id: "registry.trust.composition.summary",
    defaultMessage: "{rows} строк источника → {contours} контуров, {actors} субъектов; не связано {unlinked}",
    description: "Краткая сводка свёрнутого блока «Из чего собран канон»; все значения — числа; субъекты — хозяйства и иные владельцы земли",
  },
  compositionMore: { id: "registry.trust.composition.more", defaultMessage: "показать состав", description: "Текст кнопки раскрытия блока «Из чего собран канон»; строчными" },
  issuesTitle: { id: "registry.trust.issues.title", defaultMessage: "Что в данных не сходится", description: "Заголовок раскрывающегося блока: разрезы замечаний качества данных" },
  issuesSummary: {
    id: "registry.trust.issues.summary",
    defaultMessage: "{total, plural, one {# замечание} few {# замечания} many {# замечаний} other {# замечания}} в {codes, plural, one {# коде} other {# кодах}}; открытых {open}, блокирующих {blockers}",
    description: "Краткая сводка свёрнутого блока замечаний: {total} — всего замечаний, {codes} — число видов (кодов) замечаний, {open} — открытых, {blockers} — блокирующих",
  },
  issuesNone: { id: "registry.trust.issues.none", defaultMessage: "В срезе нет ни одного замечания качества", description: "Сводка свёрнутого блока замечаний, когда замечаний нет" },
  issuesMore: { id: "registry.trust.issues.more", defaultMessage: "показать разрезы", description: "Текст кнопки раскрытия блока замечаний; строчными" },
  issuesFootnote: {
    id: "registry.trust.issues.footnote",
    defaultMessage: "Это разрезы, а не разбор: сами строки с адресом до листа и строки файла живут на своём экране, вместе с фильтрами. <link>Разбор замечаний</link>.",
    description: "Пояснение под разрезами замечаний; <link> — ссылка на экран качества данных",
  },
});

/**
 * Сходится ли сверка.
 *
 * Равенство, напечатанное знаком «=», обязано быть проверенным: четыре числа
 * приходят из ответа сервера независимо друг от друга, и нарисовать между ними
 * «=», ни разу их не сложив, значит утверждать то, чего экран не знает.
 * Расхождение шестизначных величин глазом не ловится — ровно ради этого экран и
 * существует.
 *
 * Три исхода, а не два: `unknown` — когда хотя бы одной величины в срезе нет.
 * Отсутствие не ноль, и «не сходится» здесь было бы такой же выдумкой, как
 * «сходится».
 */
export type BalanceVerdict = "ok" | "off" | "unknown";

export function balanceCheck(
  canonMicroha: number | null,
  excludedMicroha: number | null,
  droppedMicroha: number | null,
  checkpointMicroha: number | null,
  // Части контуров, не сложенные с контуром «целиком» (инвариант 7): чекпоинт
  // сложил строки как есть, канон — нет. Ответ без поля — слагаемого нет.
  insideWholeMicroha: number | null = 0,
): { verdict: BalanceVerdict; diff: number } {
  if (
    canonMicroha === null ||
    excludedMicroha === null ||
    droppedMicroha === null ||
    checkpointMicroha === null
  ) {
    return { verdict: "unknown", diff: 0 };
  }
  // Микрогектары — целые: сравнение точное, и «почти сходится» здесь не бывает.
  const diff =
    canonMicroha + (insideWholeMicroha ?? 0) + excludedMicroha + droppedMicroha - checkpointMicroha;
  return { verdict: diff === 0 ? "ok" : "off", diff };
}

export function TrustScreen() {
  const t = useT();
  const context = useAnalysisContext();
  const summary = useRequest(
    () => api.registrySummary(context.params),
    [context.key],
  );

  if (summary.state === "loading") {
    return <DataStatePanel state="loading" title={t(m.loading)} />;
  }
  if (!summary.data) {
    return (
      <RequestErrorPanel
        error={summary.error}
        title={t(m.loadError)}
        action={<Button onClick={summary.reload}>{t(COMMON.retry)}</Button>}
      />
    );
  }

  const data = summary.data;
  const snapshot = data.snapshot;
  const total = data.areas.find((item) => item.code === "TOTAL_LAND");
  const issues = data.issues;
  // Тревожит только ОТКРЫТОЕ: разобранное замечание остаётся в истории, но
  // публикацию больше не держит.
  const open = issues.by_status.find((bucket) => bucket.key === "open")?.count ?? 0;
  const insideWhole = data.excluded.parts_inside_whole_microha ?? 0;
  const balance = balanceCheck(
    total?.area_microha ?? null,
    data.excluded.contour_no_conflict_microha,
    data.excluded.contour_no_conflict_dropped_microha,
    data.excluded.checkpoint_total_microha,
    insideWhole,
  );

  return (
    <div className={features.stack}>
      <PageHeader
        title={t(m.title)}
        breadcrumb={
          <LevelTrail
            steps={[districtStep(context.linkSearch())]}
            screen={t(m.title)}
          />
        }
        purpose={
          snapshot
            ? t(m.purposeWithSnapshot, { kind: sourceKindLabel(snapshot.source_kind).label })
            : t(m.purposeNoSnapshot)
        }
        actions={
          <Link
            to={{ pathname: "/data-quality", search: context.linkSearch() }}
          >
            <Button variant="primary">{t(m.issuesButton)}</Button>
          </Link>
        }
      />

      {/* --- Первый взгляд: принятый срез и сверка ------------------------ */}

      <div data-guide="registry-balance"><Card title={t(m.balanceTitle)} large>
        {snapshot ? (
          <DefinitionList>
            <dt>{t(m.stateOn)}</dt>
            <dd>{formatDate(snapshot.state_date)}</dd>
            <dt>{t(m.source)}</dt>
            <dd>
              {/* Вид среза по-русски: `canonical_registry` — имя вида в базе,
                  а не подпись для человека. Сам код остаётся рядом — он и есть
                  то, что спрашивают при разборе. */}
              {t(m.sourceValue, {
                file: snapshot.source_filename,
                kindLabel: sourceKindLabel(snapshot.source_kind).label,
                kindCode: snapshot.source_kind,
              })}
            </dd>
            <dt>{t(m.checksum)}</dt>
            <dd className={styles.checksum}>{snapshot.source_sha256}</dd>
            <dt>{t(m.rules)}</dt>
            <dd>
              {t(m.rulesValue, {
                version: snapshot.rules_version,
                date: formatDate(snapshot.published_at),
              })}
            </dd>
          </DefinitionList>
        ) : (
          <DataStatePanel state="missing" title={t(m.noSnapshotTitle)}>
            {t(m.noSnapshotBody)}
          </DataStatePanel>
        )}

        {/* Равенство, ради которого экран и открывают. Слагаемых три, а не
            два: строки, признанные той же землёй, из канона выброшены, но в
            итоге чекпоинта они посчитаны — без этого члена «канон +
            исключённое» не сходится с чекпоинтом, и человек читал бы ошибку
            там, где её нет. Величины стоят в одной строке именно потому, что
            смысл здесь — их совпадение, а не каждая по отдельности. */}
        <div className={styles.balance}>
          <div className={styles.balanceTerm}>
            <span className={styles.balanceLabel}>{t(m.termCanon)}</span>
            <span className={styles.balanceValue}>
              <Area microha={total?.area_microha ?? null} rounded />
            </span>
          </div>
          {insideWhole > 0 ? (
            <>
              <span className={styles.balanceSign} aria-hidden>
                +
              </span>
              <div className={styles.balanceTerm}>
                <span className={styles.balanceLabel}>{t(m.termInsideWhole)}</span>
                <span className={styles.balanceValue}>
                  <Area microha={insideWhole} rounded />
                </span>
              </div>
            </>
          ) : null}
          <span className={styles.balanceSign} aria-hidden>
            +
          </span>
          <div className={styles.balanceTerm}>
            <span className={styles.balanceLabel}>{t(m.termExcluded)}</span>
            <span className={styles.balanceValue}>
              <Area
                microha={data.excluded.contour_no_conflict_microha}
                rounded
              />
            </span>
          </div>
          <span className={styles.balanceSign} aria-hidden>
            +
          </span>
          <div className={styles.balanceTerm}>
            <span className={styles.balanceLabel}>{t(m.termDropped)}</span>
            <span className={styles.balanceValue}>
              <Area
                microha={data.excluded.contour_no_conflict_dropped_microha}
                rounded
              />
            </span>
          </div>
          {/* Знак — картинка равенства: смысл его несёт текст ниже (сходится,
              не сходится с величиной расхождения, сверять не с чем), поэтому
              для скринридера он скрыт, как и «+». */}
          <span className={styles.balanceSign} aria-hidden>
            {balance.verdict === "off" ? "≠" : "="}
          </span>
          <div className={styles.balanceTerm}>
            <span className={styles.balanceLabel}>{t(m.termCheckpoint)}</span>
            <span className={styles.balanceValue}>
              <Area
                microha={data.excluded.checkpoint_total_microha}
                rounded
              />
            </span>
          </div>
        </div>
        {balance.verdict === "off" ? (
          <InlineAlert title={t(m.offTitle)}>
            <p>
              {t(m.offBody, {
                diff: areaText(Math.abs(balance.diff)),
                direction: balance.diff > 0 ? t(m.offMore) : t(m.offLess),
              })}
            </p>
          </InlineAlert>
        ) : null}
        {/* Итог сверки — своим абзацем, а не приставкой к объяснению сервера:
            это утверждение экрана, и смешивать его с чужим текстом значило бы
            выдать одно за другое. */}
        {balance.verdict === "ok" ? (
          <p className={styles.note}>{t(m.okNote)}</p>
        ) : null}
        {balance.verdict === "unknown" ? (
          <p className={styles.note}>{t(m.unknownNote)}</p>
        ) : null}
        {insideWhole > 0 ? (
          <p className={styles.note}>
            {t(m.insideWholeNote, { contours: data.excluded.parts_inside_whole_contours ?? 0 })}
          </p>
        ) : null}
        <p className={styles.note}>{data.excluded.note}</p>
      </Card></div>

      {/* --- Четыре числа состава ----------------------------------------- */}

      <div className={`${layout.grid} ${layout.grid4}`} data-guide="registry-counts">
        <MetricCard
          hero
          label={t(m.kpiContours)}
          value={<Count value={data.counts.contours} />}
          bottom={t(m.kpiContoursNote, {
            rows: formatCount(data.counts.source_records).text,
            parts: formatCount(data.counts.contour_parts).text,
          })}
        />
        <MetricCard
          label={t(m.kpiUnlinked)}
          value={<Count value={data.excluded.contour_rows_not_linked} />}
          bottom={t(m.kpiUnlinkedNote)}
        />
        <MetricCard
          label={t(m.kpiHectares)}
          value={<Area microha={total?.area_microha ?? null} rounded />}
          bottom={
            total
              ? total.missing_rows
                ? t(m.kpiHectaresMissing, {
                    rows: total.rows,
                    missing: formatCount(total.missing_rows).text,
                  })
                : t(m.kpiHectaresNote, { rows: total.rows })
              : t(m.kpiHectaresNone)
          }
        />
        <MetricCard
          label={t(m.kpiIssues)}
          value={<Count value={open} />}
          bottom={t(m.kpiIssuesNote, {
            blockers: formatCount(issues.open_blockers).text,
            total: formatCount(issues.total).text,
          })}
        />
      </div>

      {issues.open_blockers > 0 ? (
        <InlineAlert
          title={t(m.blockersTitle, { count: formatCount(issues.open_blockers).text })}
        >
          <p>
            {t(m.blockersBody, {
              link: (chunks) => (
                <Link
                  to={{
                    pathname: "/data-quality",
                    search: context.linkSearch({
                      severity: "blocker",
                      status: "open",
                    }),
                  }}
                >
                  {chunks}
                </Link>
              ),
            })}
          </p>
        </InlineAlert>
      ) : null}

      {/* --- Раскрытия ---------------------------------------------------- */}

      <div data-guide="registry-composition"><Disclosure
        title={t(m.compositionTitle)}
        summary={t(m.compositionSummary, {
          rows: formatCount(data.counts.source_records).text,
          contours: formatCount(data.counts.contours).text,
          actors: formatCount(data.counts.actors).text,
          unlinked: formatCount(data.excluded.contour_rows_not_linked).text,
        })}
        more={t(m.compositionMore)}
      >
        <RegistrySummarySection data={data} />
      </Disclosure></div>

      <div data-guide="registry-issues"><Disclosure
        title={t(m.issuesTitle)}
        summary={
          issues.total > 0
            ? t(m.issuesSummary, {
                total: issues.total,
                codes: issues.by_code.length,
                open: formatCount(open).text,
                blockers: formatCount(issues.open_blockers).text,
              })
            : t(m.issuesNone)
        }
        more={t(m.issuesMore)}
      >
        <div className={styles.facets}>
          <QualityFacets
            bySeverity={issues.by_severity}
            byStatus={issues.by_status}
            byCode={issues.by_code}
            total={issues.total}
          />
          <p className={styles.note}>
            {t(m.issuesFootnote, {
              link: (chunks) => (
                <Link to={{ pathname: "/data-quality", search: context.linkSearch() }}>
                  {chunks}
                </Link>
              ),
            })}
          </p>
        </div>
      </Disclosure></div>
    </div>
  );
}
